import type { FC } from 'react'
import { useEffect } from 'react'

import { Table } from '@skytech/ui'
import { Td } from '@skytech/ui/Table/Td'
import { Th } from '@skytech/ui/Table/Th'
import { getQueryFilter, getSortedList } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import Highlight from '@/components/Highlight'
import { NoResults } from '@/components/NoResults'
import { ResultCount } from '@/components/ResultCount'
import { useSearch, useSortParams } from '@/hooks/searchParams'
import { useRenderLimit } from '@/hooks/use-render-limit'

import { useReportsContext } from '../index.context'

import adminQueries from '../../queries'
import type { OffboardingReport, ReportFilters } from '../../resources'
import adminResources from '../../resources'

export const OffBoarding: FC<{
  filters: ReportFilters
}> = ({ filters }) => {
  const { searchFilter } = useSearch()
  const { isReportListLoading, setCount, setIsReportListLoading } = useReportsContext()
  const { locale } = useAppContext()
  const [{ querySort, querySortBy }, { handleSort }] =
    useSortParams<keyof (typeof reportsFiltered)[number]>()

  const stringMatcher = (item: OffboardingReport) => [
    item.customerName,
    item.userName,
    item.employeeNo,
    item.assetStatus,
    item.terminationStatus,
  ]

  const { data: reports = [] } = useQuery(
    adminQueries.adminOffboardingReports(filters),
    async () => {
      const reports = await adminResources.getOffboardingReports(filters)
      setIsReportListLoading(false)
      return reports
    },
    { enabled: Boolean(filters.fromDate && filters.toDate) }
  )

  const reportsFiltered = reports.filter(reportsObject =>
    getQueryFilter(searchFilter, stringMatcher)(reportsObject)
  )
  const sortedList = getSortedList({
    list: reportsFiltered,
    querySort,
    querySortBy,
    locale,
  })

  useEffect(() => {
    setCount(reports.length)
  }, [reports, setCount])

  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList, 30)
  const total = reportsFiltered.length

  return (
    <>
      <ResultCount count={total} />
      {total > 0 ? (
        <Table>
          <thead>
            <tr>
              <Th onClick={handleSort} id="customerName">
                <Translation id="Customer Name" />
              </Th>
              <Th onClick={handleSort} id="userName">
                <Translation id="User Name" />
              </Th>
              <Th onClick={handleSort} id="employeeNo">
                <Translation id="Employee Number" />
              </Th>
              <Th onClick={handleSort} id="assetStatus">
                <Translation id="Asset Status" />
              </Th>
              <Th onClick={handleSort} id="terminationStatus">
                <Translation id="Termination Status" />
              </Th>
            </tr>
          </thead>
          <tbody>
            {visibleList.map(
              ({ customerName, userName, employeeNo, assetStatus, terminationStatus }, index) => (
                <tr key={`${employeeNo}-${index}`}>
                  <Td>
                    <Highlight text={customerName} pattern={searchFilter} />
                  </Td>
                  <Td>
                    <Highlight text={userName} pattern={searchFilter} />
                  </Td>
                  <Td>
                    <Highlight text={employeeNo} pattern={searchFilter} />
                  </Td>
                  <Td>
                    <Highlight text={assetStatus} pattern={searchFilter} />
                  </Td>
                  <Td>
                    <Highlight text={terminationStatus} pattern={searchFilter} />
                  </Td>
                </tr>
              )
            )}
          </tbody>
        </Table>
      ) : (
        !isReportListLoading && <NoResults />
      )}
      <ListLoaderComponent />
    </>
  )
}
